import { Skeleton } from 'antd';
import { arrowLeft } from 'assets';
import { Button, Modal } from 'components';
import { useAppDispatch } from 'hooks';
import { addImage } from 'pages/SiteDetail/slice';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { GetDetailImageResponse, ImageAlbum } from 'types';
import { dayjs, piexif } from 'libs';
import { useSelector } from 'react-redux';
import { imageSelectors } from 'pages/SiteDetail/selectors';
import { albumsService, siteService } from 'services';
import { ButtonCut, DetailImageContentStyled, HeaderStyled } from './styles';
interface Props {
  data?: ImageAlbum | null;
  onCancel: () => void;
  visible: boolean;
  showModal: (open: boolean) => void;
  closeModalImage: (close: boolean) => void;
  detailImage: GetDetailImageResponse | null;
  convertImage: string;
}
interface CropProps {
  aspect?: number;
}
const CutImageModal: FC<Props> = ({ visible, detailImage, closeModalImage, showModal }) => {
  const imgRef = useRef(null);
  const dispatch = useAppDispatch();
  const previewCanvasRef = useRef(null);
  const [crop, setCrop] = useState<CropProps>({ aspect: 16 / 9 });
  const [convertImage, setConvertImage] = useState('');
  const [completedCrop, setCompletedCrop] = useState({ width: 0, height: 0, x: 0, y: 0 });
  const [loading, setLoading] = useState<boolean>(true);
  const images = useSelector(imageSelectors.selectAll);

  const onLoad = useCallback((img) => {
    imgRef.current = img;
  }, []);
  const cutImages = useCallback(async () => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current || !detailImage) {
      return;
    }

    const image = imgRef.current as HTMLImageElement;
    const canvas = previewCanvasRef.current as HTMLCanvasElement;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio;
    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    ctx?.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    if (ctx !== null) {
      ctx.imageSmoothingQuality = 'high';
    }
    ctx?.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );
    const exif: Record<number, string> = {};
    exif[piexif.ExifIFD.DateTimeOriginal] = JSON.parse(detailImage?.exif_text)?.DateTimeOriginal;
    exif[piexif.ExifIFD.DateTimeDigitized] = JSON.parse(detailImage?.exif_text)?.DateTimeDigitized;

    const exif_text = JSON.stringify({
      ...JSON.parse(detailImage.exif_text),
      DateTime: JSON.parse(detailImage?.exif_text)?.DateTime,
      DateTimeOriginal: JSON.parse(detailImage?.exif_text)?.DateTimeOriginal,
      DateTimeDigitized: JSON.parse(detailImage?.exif_text)?.DateTimeDigitized,
    });
    const gpsIfd: Record<number, any> = {};
    const lat = JSON.parse(detailImage.exif_text).latitude;
    const lng = JSON.parse(detailImage.exif_text).longitude;
    gpsIfd[piexif.GPSIFD.GPSLatitudeRef] = lat < 0 ? 'S' : 'N';
    gpsIfd[piexif.GPSIFD.GPSLatitude] = piexif.GPSHelper.degToDmsRationalv1(lat);
    gpsIfd[piexif.GPSIFD.GPSLongitudeRef] = lng < 0 ? 'W' : 'E';
    gpsIfd[piexif.GPSIFD.GPSLongitude] = piexif.GPSHelper.degToDmsRationalv1(lng);
    const exifObj = { Exif: exif, GPS: gpsIfd };
    const exifStr = piexif.dump(exifObj);
    const base64Image = canvas.toDataURL('image/jpeg');
    const base64 = piexif.insert(exifStr, base64Image);
    const res = await albumsService.addImages({
      building_id: `${detailImage?.building_id}`,
      exif_text,
      folder_id: `${detailImage?.folder_id}`,
      image_binary: base64,
      taken_date: `${detailImage?.taken_date}`,
      edited_date: `${dayjs()}`,
    });
    if (res?.data) {
      dispatch(addImage([res.data, ...images]));
      if (res?.data?.testing_flag === 1) {
        siteService.uploadImages({ image_id: [res.data?.image_id] });
      }
    }

    setCrop({ aspect: 0 });
    setCompletedCrop({ width: 0, height: 0, x: 0, y: 0 });
    closeModalImage(false);
  }, [completedCrop, images, detailImage, closeModalImage, dispatch]);

  const getBase64FromUrl = async (url: '' | undefined | string) => {
    try {
      if (url) {
        const data = await fetch(url);
        const blob = await data.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            const base64data = reader.result;
            resolve(base64data);
          };
        });
      }
    } catch (error) {
      return null;
    }
  };
  useEffect(() => {
    setConvertImage('');
    setLoading(true);
    const f = async () => {
      const convertItemImage =
        detailImage?.image_url &&
        detailImage?.image_url.replace(
          process.env.REACT_APP_S3_RES_URL || '',
          process.env.REACT_APP_RES_URL || ''
        );
      const imgBase: any = await getBase64FromUrl(convertItemImage);
      setLoading(!convertItemImage);
      imgBase && setConvertImage(imgBase);
    };
    f();
  }, [detailImage]);

  const onPrevious = () => {
    setCrop({ aspect: 0 });
    setCompletedCrop({ width: 0, height: 0, x: 0, y: 0 });
    closeModalImage(false);
    showModal(true);
  };
  const onCancelCutImage = () => {
    setCrop({ aspect: 0 });
    setCompletedCrop({ width: 0, height: 0, x: 0, y: 0 });
    setLoading(false);
    closeModalImage(false);
  };

  return (
    <Modal
      title={
        <HeaderStyled onClick={onCancelCutImage}>
          <img src={arrowLeft} alt="arrowLeft" />
          <div>写真を切り抜き</div>
        </HeaderStyled>
      }
      visible={visible}
      footer={false}
      width={648}
    >
      <DetailImageContentStyled>
        <div className="image-crop">
          {!loading ? (
            detailImage?.image_url && (
              <ReactCrop
                src={convertImage}
                onImageLoaded={onLoad}
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
              />
            )
          ) : (
            <Skeleton.Button active style={{ width: 600, height: 450, maxWidth: '100%' }} />
          )}
        </div>
      </DetailImageContentStyled>
      <div className="ant-modal-footer">
        <Button onClick={onPrevious}>キャンセル</Button>
        <ButtonCut
          onClick={cutImages}
          disabled={!completedCrop?.width || !completedCrop?.height || !convertImage}
        >
          保存
        </ButtonCut>
      </div>
      <canvas ref={previewCanvasRef} style={{ display: 'contents' }} />
    </Modal>
  );
};

export default CutImageModal;
